import React from 'react';
import { useTranslation } from 'react-i18next';
import { Thumbnail } from '@remotion/player';
import { Download, Loader2 } from 'lucide-react';
import { QuoteSlide } from '@/remotion/carousel/QuoteSlide';
import type { QuoteSlideProps } from '@/lib/quoteCarousel/buildQuoteSlideProps';
import { cn } from '@/lib/utils';

interface SlideFilmstripProps {
  /** One entry per slide, in carousel order. */
  items: { id: string; props: QuoteSlideProps }[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** "Baixar imagem" for one slide; absent = no per-slide download. */
  onDownload?: (id: string) => void;
  /** Slide whose image download is in progress. */
  downloadingId?: string | null;
}

/** Row of small still thumbnails, one per slide. Uses Remotion's
 *  `<Thumbnail>` (a single static frame, no playback machinery) rather than a
 *  Player per slide — up to 10 of these mount at once. */
export const SlideFilmstrip: React.FC<SlideFilmstripProps> = ({
  items, selectedId, onSelect, onDownload, downloadingId = null,
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex gap-2 overflow-x-auto pb-2" data-testid="quote-slide-filmstrip">
      {items.map(({ id, props }, index) => (
        // The download action is a SIBLING of the select button (no nested
        // interactive elements), revealed on hover/focus.
        <div key={id} className="group relative shrink-0">
          <button
            type="button"
            data-testid={`quote-filmstrip-${index}`}
            aria-label={t('quoteCarousel.slides.slideLabel', { number: index + 1 })}
            aria-pressed={selectedId === id}
            onClick={() => onSelect(id)}
            className={cn(
              'relative block w-20 overflow-hidden rounded-md border-2 bg-muted transition-colors',
              selectedId === id ? 'border-primary' : 'border-transparent hover:border-border',
            )}
            style={{ aspectRatio: `${props.width} / ${props.height}` }}
          >
            <Thumbnail
              component={QuoteSlide as unknown as React.FC<Record<string, unknown>>}
              inputProps={props}
              frameToDisplay={0}
              durationInFrames={1}
              fps={30}
              compositionWidth={props.width}
              compositionHeight={props.height}
              style={{ width: '100%', height: '100%' }}
            />
            <span className="absolute bottom-0.5 left-1 rounded bg-background/80 px-1 text-[10px] font-medium">
              {index + 1}
            </span>
          </button>
          {onDownload && (
            <button
              type="button"
              data-testid={`quote-filmstrip-download-${index}`}
              aria-label={t('quoteCarousel.singleImage.downloadSlide', { number: index + 1 })}
              title={t('quoteCarousel.singleImage.downloadSlide', { number: index + 1 })}
              disabled={downloadingId !== null}
              onClick={() => onDownload(id)}
              className={cn(
                'absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded bg-background/90 text-foreground shadow-sm transition-opacity',
                'focus-visible:opacity-100 group-hover:opacity-100 disabled:cursor-not-allowed',
                downloadingId === id ? 'opacity-100' : 'opacity-0',
              )}
            >
              {downloadingId === id ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5" />
              )}
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export default SlideFilmstrip;
